import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const API   = process.env.REACT_APP_API_URL;
const token = () => JSON.parse(localStorage.getItem("edo-admin"))?.token;

const CATEGORIES = ["career", "interview", "skills", "cv", "networking", "kids", "general"];
const TYPES      = ["article", "video", "social", "document", "website"];

const STATUS_STYLE = {
  pending:    "bg-yellow-100 text-yellow-700",
  processing: "bg-blue-100 text-blue-700 animate-pulse",
  active:     "bg-green-100 text-green-700",
  failed:     "bg-red-100 text-red-700",
};

const CAT_ICON = {
  career: "💼", interview: "🎤", skills: "🛠️", cv: "📄",
  networking: "🤝", kids: "🌟", general: "📚",
};

export default function KnowledgeBase() {
  const { t } = useTranslation();
  const [sources, setSources]   = useState([]);
  const [meta, setMeta]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmit] = useState(false);
  const [search, setSearch]     = useState("");
  const [filterCat, setFilter]  = useState("");
  const [showForm, setForm]     = useState(false);
  const [expanded, setExpanded] = useState(null);

  const [form, setFormData] = useState({
    url: "", title: "", category: "general",
    content_type: "article", description: "",
  });

  const auth = { headers: { Authorization: `Bearer ${token()}` } };

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/edolutions/admin/knowledge`, auth);
      const data = res.data.data;
      setSources(data.data ?? data);
      setMeta({ total: data.total, last_page: data.last_page, current_page: data.current_page });
    } catch {
      toast.error(t("adminDash.knowledgeBase.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.url || !form.title) return toast.warn(t("adminDash.knowledgeBase.urlTitleRequired"));
    setSubmit(true);
    try {
      await axios.post(`${API}/api/edolutions/admin/knowledge`, form, auth);
      toast.success(t("adminDash.knowledgeBase.sourceAddedProcessed"));
      setFormData({ url: "", title: "", category: "general", content_type: "article", description: "" });
      setForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || t("adminDash.knowledgeBase.addFailed"));
    } finally {
      setSubmit(false);
    }
  };

  const handleRefresh = async (id) => {
    try {
      toast.info(t("adminDash.knowledgeBase.refetchingContent"));
      await axios.post(`${API}/api/edolutions/admin/knowledge/${id}/refresh`, {}, auth);
      toast.success(t("adminDash.knowledgeBase.sourceRefreshed"));
      load();
    } catch {
      toast.error(t("adminDash.knowledgeBase.refreshFailed"));
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(t("adminDash.knowledgeBase.confirmDelete", { title }))) return;
    try {
      await axios.delete(`${API}/api/edolutions/admin/knowledge/${id}`, auth);
      toast.success(t("adminDash.demoClass.deletedSuccess"));
      load();
    } catch {
      toast.error(t("adminDash.knowledgeBase.deleteFailed"));
    }
  };

  const visible = sources.filter(s =>
    (!filterCat || s.category === filterCat) &&
    (!search || s.title.toLowerCase().includes(search.toLowerCase()) ||
                (s.url || "").toLowerCase().includes(search.toLowerCase()))
  );

  const totalActive = sources.filter(s => s.status === "active").length;
  const totalWords  = sources.reduce((a, s) => a + (s.word_count || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t("adminDash.knowledgeBase.title")}</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {t("adminDash.knowledgeBase.subtitle")}
          </p>
        </div>
        <button
          onClick={() => setForm(f => !f)}
          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2.5 rounded-xl transition">
          <span className="text-lg">+</span> {t("adminDash.knowledgeBase.addSource")}
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon="📚" label={t("adminDash.knowledgeBase.totalSources")} value={sources.length} />
        <Stat icon="✅" label={t("adminDash.knowledgeBase.active")} value={totalActive} color="green" />
        <Stat icon="📝" label={t("adminDash.knowledgeBase.totalWords")} value={totalWords.toLocaleString()} color="blue" />
        <Stat icon="🧠" label={t("adminDash.knowledgeBase.aiUsesThis")} value={t("adminDash.knowledgeBase.sourcesCount", { count: totalActive })} color="purple" />
      </div>

      {/* ── Add Form ── */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-purple-100">
          <h2 className="font-bold text-gray-800 text-lg mb-4">{t("adminDash.knowledgeBase.addKnowledgeSource")}</h2>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">{t("adminDash.knowledgeBase.urlRequired")}</label>
                <input type="url" placeholder="https://example.com/article"
                  value={form.url} onChange={e => setFormData({ ...form, url: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-400 outline-none" />
                <p className="text-xs text-gray-400 mt-1">{t("adminDash.knowledgeBase.urlHelpText")}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">{t("adminDash.knowledgeBase.titleRequired")}</label>
                <input type="text" placeholder="e.g. 'How to write a great CV — Forbes 2025'"
                  value={form.title} onChange={e => setFormData({ ...form, title: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-400 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">{t("adminDash.knowledgeBase.category")}</label>
                <select value={form.category} onChange={e => setFormData({ ...form, category: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-400 outline-none bg-white">
                  {CATEGORIES.map(c => <option key={c} value={c}>{CAT_ICON[c]} {c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">{t("adminDash.knowledgeBase.contentType")}</label>
                <select value={form.content_type} onChange={e => setFormData({ ...form, content_type: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-400 outline-none bg-white">
                  {TYPES.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">{t("adminDash.knowledgeBase.description")} <span className="text-gray-400 font-normal">({t("adminDash.knowledgeBase.optional")})</span></label>
                <textarea rows={2} placeholder={t("adminDash.knowledgeBase.descriptionPlaceholder")}
                  value={form.description} onChange={e => setFormData({ ...form, description: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-400 outline-none resize-none" />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition disabled:opacity-50">
                {submitting ? t("adminDash.knowledgeBase.fetchingProcessing") : t("adminDash.knowledgeBase.addToKnowledgeBase")}
              </button>
              <button type="button" onClick={() => setForm(false)}
                className="text-gray-500 hover:text-gray-700 text-sm px-4 py-2.5 rounded-xl border border-gray-200 transition">
                {t("adminDash.common.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input type="text" placeholder={t("adminDash.knowledgeBase.searchPlaceholder")}
          value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-400 outline-none" />
        <select value={filterCat} onChange={e => setFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-purple-400 outline-none">
          <option value="">{t("adminDash.knowledgeBase.allCategories")}</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{CAT_ICON[c]} {c}</option>)}
        </select>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3 animate-spin inline-block">⚙️</div>
          <p>{t("adminDash.knowledgeBase.loadingKnowledgeBase")}</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl">
          <div className="text-5xl mb-3">📭</div>
          <p className="text-gray-500 font-medium">{t("adminDash.knowledgeBase.noSourcesYet")}</p>
          <p className="text-gray-400 text-sm mt-1">{t("adminDash.knowledgeBase.addFirstUrlHint")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(s => (
            <div key={s.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Row */}
              <div className="flex items-start gap-4 p-4 cursor-pointer hover:bg-gray-50 transition"
                onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                <span className="text-2xl mt-0.5 flex-shrink-0">{CAT_ICON[s.category] || "📚"}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-0.5">
                    <span className="font-semibold text-gray-800 text-sm truncate">{s.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[s.status]}`}>
                      {s.status}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{s.category}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{s.content_type}</span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{s.url}</p>
                  {s.status === "active" && (
                    <p className="text-xs text-green-600 mt-0.5">
                      {s.chunks_count ?? "?"} chunks · {(s.word_count || 0).toLocaleString()} words
                      {s.last_fetched && ` · updated ${new Date(s.last_fetched).toLocaleDateString()}`}
                    </p>
                  )}
                  {s.status === "failed" && s.error_msg && (
                    <p className="text-xs text-red-500 mt-0.5">Error: {s.error_msg}</p>
                  )}
                </div>
                <span className="text-gray-300 text-xs mt-1">{expanded === s.id ? "▲" : "▼"}</span>
              </div>

              {/* Expanded actions */}
              {expanded === s.id && (
                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 flex flex-wrap items-center gap-3">
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-purple-600 hover:underline font-medium">
                    {t("adminDash.knowledgeBase.openUrl")} ↗
                  </a>
                  <button onClick={() => handleRefresh(s.id)}
                    className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold px-3 py-1.5 rounded-lg transition">
                    🔄 {t("adminDash.knowledgeBase.refetch")}
                  </button>
                  <button onClick={() => handleDelete(s.id, s.title)}
                    className="text-xs bg-red-50 hover:bg-red-100 text-red-600 font-semibold px-3 py-1.5 rounded-lg transition">
                    🗑 {t("adminDash.common.delete")}
                  </button>
                  {s.description && (
                    <p className="w-full text-xs text-gray-500 mt-1">📝 {s.description}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── KB Info Banner ── */}
      <div className="mt-8 bg-purple-50 border border-purple-200 rounded-2xl p-5">
        <h3 className="font-bold text-purple-800 mb-2">💡 {t("adminDash.knowledgeBase.howItWorksTitle")}</h3>
        <ul className="text-sm text-purple-700 space-y-1.5">
          <li>• {t("adminDash.knowledgeBase.howItWorks1")}</li>
          <li>• {t("adminDash.knowledgeBase.howItWorks2")}</li>
          <li>• {t("adminDash.knowledgeBase.howItWorks3")}</li>
          <li>• {t("adminDash.knowledgeBase.howItWorks4")}</li>
          <li>• {t("adminDash.knowledgeBase.howItWorks5")}</li>
        </ul>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, color = "gray" }) {
  const colors = {
    gray:   "bg-gray-100 text-gray-700",
    green:  "bg-green-50 text-green-700",
    blue:   "bg-blue-50 text-blue-700",
    purple: "bg-purple-50 text-purple-700",
  };
  return (
    <div className={`${colors[color]} rounded-2xl p-4`}>
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-xl font-extrabold">{value}</div>
      <div className="text-xs font-medium opacity-70 mt-0.5">{label}</div>
    </div>
  );
}
